import { DataWithPagination } from "services/api/types";
import { Pagination } from "services/search";

import { createApiCall, paginatedApiCall } from "services/api/createApiCall";

import { transformErrorResponse } from "services/api/transformation";
import {
  transformOffice,
  transformOfficeAddress,
  transformOfficeAddressOnCreate,
  transformOfficeAddressOnUpdate,
  transformOfficeAddressRow,
  transformOfficeEmail,
  transformOfficeEmailOnCreate,
  transformOfficeEmailOnUpdate,
  transformOfficeEmailRow,
  transformOfficeNote,
  transformOfficeNoteOnCreate,
  transformOfficeNoteOnUpdate,
  transformOfficeNoteRow,
  transformOfficeOnCreate,
  transformOfficeOnUpdate,
  transformOfficePhone,
  transformOfficePhoneOnCreate,
  transformOfficePhoneOnUpdate,
  transformOfficePhoneRow,
  transformOfficeRow,
} from "./transformations";
import {
  Office,
  OfficeAddress,
  OfficeAddressEditFormValues,
  OfficeAddressRow,
  OfficeEditFormValues,
  OfficeEmail,
  OfficeEmailEditFormValues,
  OfficeEmailRow,
  OfficeNote,
  OfficeNoteEditFormValues,
  OfficeNoteRow,
  OfficePhone,
  OfficePhoneEditFormValues,
  OfficePhoneRow,
  OfficeRow,
} from "./types";
import { UserRow } from "services/api/users/types";
import { transformUserRow } from "services/api/users/transformations";

/*---------------------------------------------------------------------------------------
    OFFICE - SEARCH
-----------------------------------------------------------------------------------------*/
export const getOfficesAPI = async (
  pagination: Pagination,
  name?: string | null,
  tenantId?: string | null
): Promise<DataWithPagination<OfficeRow>> => {
  let path = `/v1/offices?name=`;

  if (name) path += `${name}`;
  if (tenantId) path += `&tenantId=${tenantId}`;
  return await paginatedApiCall(path, pagination, transformOfficeRow);
};

/*---------------------------------------------------------------------------------------
    OFFICE - GET BY ID
-----------------------------------------------------------------------------------------*/
export const getOfficeAPI = async (officeId: string): Promise<Office> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOffice(response.data);
};

/*---------------------------------------------------------------------------------------
    OFFICE - CREATE
-----------------------------------------------------------------------------------------*/
export const createOfficeAPI = async (
  formValues: OfficeEditFormValues,
  tenantId?: string
): Promise<Office> => {
  const response = await createApiCall(
    {
      path: "/v1/offices",
      method: "POST",
      body: {
        ...transformOfficeOnCreate(formValues),
        ...(tenantId ? { tenantId } : {}),
      },
    },
    {
      getMessage: {
        success: () => "Poslovnica je uspješno kreirana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOffice(response.data);
};

/*---------------------------------------------------------------------------------------
    OFFICE - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateOfficeAPI = async (
  officeId: string,
  formValues: OfficeEditFormValues
): Promise<Office> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}`,
      method: "PUT",
      body: transformOfficeOnUpdate(formValues),
    },
    {
      getMessage: {
        success: () => "Poslovnica je uspješno ažurirana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOffice(response.data);
};

/*---------------------------------------------------------------------------------------
    OFFICE - DELETE
-----------------------------------------------------------------------------------------*/
export const deleteOfficeAPI = async (
  officeId: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Poslovnica je uspješno obrisana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
    EMAILS
-----------------------------------------------------------------------------------------*/
export const getOfficeEmailsAPI = async (
  pagination: Pagination,
  officeId: string,
  name?: string | null
): Promise<DataWithPagination<OfficeEmailRow>> => {
  let path = `/v1/offices/${officeId}/emails?name=`;

  if (name) path += `${name}`;
  return await paginatedApiCall(path, pagination, transformOfficeEmailRow);
};

export const getOfficeEmailAPI = async (
  officeId: string,
  emailId: string
): Promise<OfficeEmail> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/emails/${emailId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficeEmail(response.data);
};

export const createOfficeEmailAPI = async (
  officeId: string,
  formValues: OfficeEmailEditFormValues
): Promise<OfficeEmail> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/emails/`,
      method: "POST",
      body: transformOfficeEmailOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => "Email je uspješno kreiran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficeEmail(response.data);
};

export const updateOfficeEmailAPI = async (
  officeId: string,
  emailId: string,
  formValues: OfficeEmailEditFormValues
): Promise<OfficeEmail> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/emails/${emailId}`,
      method: "PUT",
      body: transformOfficeEmailOnUpdate(formValues),
    },
    {
      getMessage: {
        success: () => "Email je uspješno ažuriran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficeEmail(response.data);
};

export const deleteOfficeEmailAPI = async (
  t: (key: string) => string,
  officeId: string,
  emailId: string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/emails/${emailId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Email je uspješno obrisan",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
    PHONES
-----------------------------------------------------------------------------------------*/
export const getOfficePhonesAPI = async (
  pagination: Pagination,
  officeId: string,
  name?: string | null
): Promise<DataWithPagination<OfficePhoneRow>> => {
  let path = `/v1/offices/${officeId}/phones?name=`;

  if (name) path += `${name}`;
  return await paginatedApiCall(path, pagination, transformOfficePhoneRow);
};

export const getOfficePhoneAPI = async (
  officeId: string,
  phoneId: string
): Promise<OfficePhone> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/phones/${phoneId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficePhone(response.data);
};

export const createOfficePhoneAPI = async (
  officeId: string,
  formValues: OfficePhoneEditFormValues
): Promise<OfficePhone> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/phones/`,
      method: "POST",
      body: transformOfficePhoneOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => "Telefon je uspješno kreiran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficePhone(response.data);
};

export const updateOfficePhoneAPI = async (
  officeId: string,
  phoneId: string,
  formValues: OfficePhoneEditFormValues
): Promise<OfficePhone> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/phones/${phoneId}`,
      method: "PUT",
      body: transformOfficePhoneOnUpdate(formValues),
    },
    {
      getMessage: {
        success: () => "Telefon je uspješno ažuriran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficePhone(response.data);
};

export const deleteOfficePhoneAPI = async (
  t: (key: string) => string,
  officeId: string,
  phoneId: string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/phones/${phoneId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Telefon je uspješno obrisan",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
    ADDRESSES
-----------------------------------------------------------------------------------------*/
export const getOfficeAddressesAPI = async (
  pagination: Pagination,
  officeId: string,
  name?: string | null
): Promise<DataWithPagination<OfficeAddressRow>> => {
  let path = `/v1/offices/${officeId}/addresses?name=`;

  if (name) path += `${name}`;
  return await paginatedApiCall(path, pagination, transformOfficeAddressRow);
};

export const getOfficeAddressAPI = async (
  officeId: string,
  addressId: string
): Promise<OfficeAddress> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/addresses/${addressId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficeAddress(response.data);
};

export const createOfficeAddressAPI = async (
  officeId: string,
  formValues: OfficeAddressEditFormValues
): Promise<OfficeAddress> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/addresses/`,
      method: "POST",
      body: transformOfficeAddressOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => "Adresa je uspješno kreirana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficeAddress(response.data);
};

export const updateOfficeAddressAPI = async (
  officeId: string,
  addressId: string,
  formValues: OfficeAddressEditFormValues
): Promise<OfficeAddress> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/addresses/${addressId}`,
      method: "PUT",
      body: transformOfficeAddressOnUpdate(formValues),
    },
    {
      getMessage: {
        success: () => "Adresa je uspješno ažurirana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficeAddress(response.data);
};

export const deleteOfficeAddressAPI = async (
  t: (key: string) => string,
  officeId: string,
  addressId: string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/addresses/${addressId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Adresa je uspješno obrisana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
    NOTES
-----------------------------------------------------------------------------------------*/
export const getOfficeNotesAPI = async (
  pagination: Pagination,
  officeId: string,
  name?: string | null
): Promise<DataWithPagination<OfficeNoteRow>> => {
  let path = `/v1/offices/${officeId}/notes?name=`;

  if (name) path += `${name}`;
  return await paginatedApiCall(path, pagination, transformOfficeNoteRow);
};

export const getOfficeNoteAPI = async (
  officeId: string,
  noteId: string
): Promise<OfficeNote> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/notes/${noteId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficeNote(response.data);
};

export const createOfficeNoteAPI = async (
  officeId: string,
  formValues: OfficeNoteEditFormValues
): Promise<OfficeNote> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/notes/`,
      method: "POST",
      body: transformOfficeNoteOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => "Bilješka je uspješno kreirana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficeNote(response.data);
};

export const updateOfficeNoteAPI = async (
  officeId: string,
  noteId: string,
  formValues: OfficeNoteEditFormValues
): Promise<OfficeNote> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/notes/${noteId}`,
      method: "PUT",
      body: transformOfficeNoteOnUpdate(formValues),
    },
    {
      getMessage: {
        success: () => "Bilješka je uspješno ažurirana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformOfficeNote(response.data);
};

export const deleteOfficeNoteAPI = async (
  t: (key: string) => string,
  officeId: string,
  noteId: string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/offices/${officeId}/notes/${noteId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Bilješka je uspješno obrisana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
    USERS (sustavski korisnici poslovnice — filtrirani po officeId)
-----------------------------------------------------------------------------------------*/
export const getOfficeUsersAPI = async (
  officeId: string
): Promise<UserRow[]> => {
  const response = await createApiCall({
    path: `/v1/offices/${officeId}/users`,
    method: "GET",
  })();

  return (response.data?.results ?? []).map(transformUserRow);
};
